/**
 * WeeklyLife API 模組
 *
 * 提供每週生活建議相關的 API 功能：
 * - 取得本週生活建議
 * - 查詢歷史週次建議
 *
 * @module api/weeklylife
 */

import { apiClient } from './client';

/**
 * 餐點項目介面
 */
export interface MealItem {
  id?: number;
  meal_id?: number;
  name: string;
  description?: string;
  ingredients?: string;
  nutrition_benefits?: string;
  preparation_time?: string;
  category?: string;
}

/**
 * 靜思反省項目介面
 */
export interface ReflectionItem {
  id: number;
  question: string;
  category?: string;
  recommended_time?: string;
}

/**
 * 週末活動介面
 */
export interface WeekendActivity {
  id: number;
  activity_name: string;
  activity_type: 'static' | 'dynamic';
  description?: string;
  duration?: string;
  benefits?: string;
  location_type?: string;
}

/**
 * 節氣飲品介面
 */
export interface SolarTermDrink {
  id: number;
  drink_name: string;
  ingredients?: string;
  benefits?: string;
  preparation?: string;
  best_time?: string;
}

/**
 * 本週建議回應介面
 */
export interface WeeklyAdviceResponse {
  success: boolean;
  message?: string;
  week_start?: string;
  week_end?: string;
  meals?: {
    weekday_breakfasts: MealItem[];
    weekday_lunches: MealItem[];
    weekday_dinners: MealItem[];
    weekend_breakfast: MealItem | null;
    weekend_lunch: MealItem | null;
    weekend_dinner: MealItem | null;
  };
  reflections?: ReflectionItem[];
  activities?: {
    static_activity: WeekendActivity | null;
    dynamic_activity: WeekendActivity | null;
  };
  ziwei_advice?: {
    lowest_gong_name: string;
    lowest_gong_advice: string;
  };
  solar_term?: {
    name: string;
    drink: SolarTermDrink | null;
  };
  created_at?: string;
}

/**
 * 歷史建議項目介面
 */
export interface WeeklyAdviceHistoryItem {
  schedule_id: number;
  week_start: string;
  week_end: string;
  lowest_gong_name: string;
  lowest_gong_advice: string;
  solar_term: string;
  created_at: string;
}

/**
 * 歷史建議回應介面
 */
export interface WeeklyAdviceHistoryResponse {
  success: boolean;
  count: number;
  history: WeeklyAdviceHistoryItem[];
}

/**
 * 取得本週生活建議
 *
 * @param targetDate 目標日期 (YYYY-MM-DD)，預設為今天
 * @returns 本週生活建議
 *
 * @example
 * ```typescript
 * const advice = await getWeeklyAdvice();
 * console.log(advice.meals.weekday_breakfasts);
 * ```
 */
export const getWeeklyAdvice = async (targetDate?: string): Promise<WeeklyAdviceResponse> => {
  const params = targetDate ? { target_date: targetDate } : {};
  const response = await apiClient.get<WeeklyAdviceResponse>('/api/weekly-life', { params });
  return response as any as WeeklyAdviceResponse;
};

/**
 * 查詢歷史週次建議
 *
 * @param weeks 要查詢的週數，預設 4
 * @returns 歷史建議列表
 *
 * @example
 * ```typescript
 * const history = await getWeeklyAdviceHistory(8);
 * console.log(`共 ${history.count} 筆歷史記錄`);
 * ```
 */
export const getWeeklyAdviceHistory = async (weeks: number = 4): Promise<WeeklyAdviceHistoryResponse> => {
  const response = await apiClient.get<WeeklyAdviceHistoryResponse>(
    '/api/weekly-life/history',
    {
      params: { weeks }
    }
  );
  return response as any as WeeklyAdviceHistoryResponse;
};

/**
 * 格式化週次顯示
 *
 * @param weekStart 週開始日期字串 (YYYY-MM-DD)
 * @param weekEnd 週結束日期字串 (YYYY-MM-DD)
 * @returns 格式化的週次字串
 *
 * @example
 * ```typescript
 * formatWeekRange('2026-01-13', '2026-01-19')
 * // 返回: "2026/01/13 ~ 2026/01/19"
 * ```
 */
export const formatWeekRange = (weekStart: string, weekEnd: string): string => {
  const start = weekStart.replace(/-/g, '/');
  const end = weekEnd.replace(/-/g, '/');
  return `${start} ~ ${end}`;
};

/**
 * 取得星期幾的中文名稱
 *
 * @param dayIndex 星期索引 (0-6, 0=週一)
 * @returns 中文星期名稱
 */
export const getWeekdayName = (dayIndex: number): string => {
  const weekdays = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];
  return weekdays[dayIndex] || '';
};

/**
 * 判斷是否為工作日
 *
 * @param dayIndex 星期索引 (0-6, 0=週一)
 * @returns true 表示工作日，false 表示週末
 */
export const isWeekday = (dayIndex: number): boolean => {
  return dayIndex >= 0 && dayIndex <= 4;
};

/**
 * 取得餐點類型的顯示名稱
 *
 * @param mealType 餐點類型 ('breakfast', 'lunch', 'dinner')
 * @returns 中文餐點名稱
 */
export const getMealTypeName = (mealType: 'breakfast' | 'lunch' | 'dinner'): string => {
  const mealNames = {
    breakfast: '早餐',
    lunch: '午餐',
    dinner: '晚餐',
  };
  return mealNames[mealType] || mealType;
};

/**
 * 取得活動類型的顯示名稱
 *
 * @param activityType 活動類型 ('static' | 'dynamic')
 * @returns 中文活動類型名稱
 */
export const getActivityTypeName = (activityType: 'static' | 'dynamic'): string => {
  return activityType === 'static' ? '靜態活動' : '動態活動';
};

/**
 * 格式化營養資訊
 *
 * @param nutritionBenefits 營養好處字串
 * @returns 格式化的營養資訊陣列
 */
export const formatNutritionBenefits = (nutritionBenefits?: string): string[] => {
  if (!nutritionBenefits) return [];

  // 分割多個營養資訊（假設用逗號或分號分隔）
  return nutritionBenefits
    .split(/[,;]/)
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

/**
 * 格式化食材列表
 *
 * @param ingredients 食材字串
 * @returns 格式化的食材陣列
 */
export const formatIngredients = (ingredients?: string): string[] => {
  if (!ingredients) return [];

  return ingredients
    .split(/[,;]/)
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

/**
 * 取得本週是第幾週
 *
 * @param date 日期物件，預設為今天
 * @returns 週次資訊 {year: number, week: number}
 */
export const getWeekNumber = (date: Date = new Date()): { year: number; week: number } => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
};

/**
 * 檢查是否有本週建議資料
 *
 * @param advice 本週建議回應
 * @returns true 表示有資料，false 表示無資料
 */
export const hasWeeklyAdvice = (advice: WeeklyAdviceResponse | null): boolean => {
  if (!advice || !advice.success) return false;
  return !advice.message || advice.message !== '本週尚未產生生活建議';
};

/**
 * 取得餐點總數
 *
 * @param advice 本週建議回應
 * @returns 餐點總數
 */
export const getTotalMeals = (advice: WeeklyAdviceResponse): number => {
  if (!advice.meals) return 0;

  const weekdayCount =
    (advice.meals.weekday_breakfasts?.length || 0) +
    (advice.meals.weekday_lunches?.length || 0) +
    (advice.meals.weekday_dinners?.length || 0);

  const weekendCount =
    (advice.meals.weekend_breakfast ? 1 : 0) +
    (advice.meals.weekend_lunch ? 1 : 0) +
    (advice.meals.weekend_dinner ? 1 : 0);

  return weekdayCount + weekendCount;
};

/**
 * 取得活動總數
 *
 * @param advice 本週建議回應
 * @returns 活動總數
 */
export const getTotalActivities = (advice: WeeklyAdviceResponse): number => {
  if (!advice.activities) return 0;

  return (
    (advice.activities.static_activity ? 1 : 0) +
    (advice.activities.dynamic_activity ? 1 : 0)
  );
};

/**
 * 取得反省項目總數
 *
 * @param advice 本週建議回應
 * @returns 反省項目總數
 */
export const getTotalReflections = (advice: WeeklyAdviceResponse): number => {
  return advice.reflections?.length || 0;
};
